import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Col, Collapse } from 'reactstrap'
import PublicSection from './publicSection'

import './Styles/publicSidebar.scss' // Create a separate CSS file for styling if needed
import { BiArrowToLeft, BiArrowToRight } from 'react-icons/bi'
import { primary_color } from '../../../Helper/uiHelper'
import useMediaQuery from '../../../CustomHook/useMediaQuery'
import { setToggleLeftSideBar } from '../../../Redux/Reducers/sideBar'
import { getLocalStorageItem } from '../../../Helper/cookieHelper'
import { RESERVATION_DEFAULT_DURATION_SECONDS } from '../../../Helper/reservationConstants'
import { request } from '../../../Redux/Sagas/requests/api'
import { setNotification } from '../../../Redux/Reducers/notificationHandling'
import { useHistory } from 'react-router-dom'

const SIDEBAR_WIDGET_TYPES_SKIP_COLLAPSED_CTA = new Set(['booking_timer', 'old_orders', 'provisional_booking', 'spacer', 'divider'])

const SIDEBAR_COLLAPSED_CTA_LABELS = {
  login_form: 'Login',
  public_auth: 'Your details',
  checkout_form: 'Booking',
  address_dropdown: 'Address',
  payment_method: 'Payment',
  payment_details: 'Summary',
  booking_details: 'Booking',
  apply_coupon: 'Coupon',
  navigation: 'Steps',
  search_result: 'Results',
  search_filters: 'Filters',
  hero_search: 'Search',
  payment_buttons: 'Actions',
  student_details: 'Students',
  company_details: 'Details',
  payment_success: 'Success',
  payment_success_image: 'Open',
  payment_success_action: 'Next',
  email_details: 'Email',
  resend_email: 'Resend',
  hero_bg: 'Banner',
  raise_ticket_form: 'Support',
  faq_listing: 'FAQ',
  number_list: 'Info',
  button: 'More',
  content: 'More',
  home_page_about_us: 'About',
  about_us_page_about_us: 'About',
  about_us_page_perfect: 'About',
  content_image: 'Image',
  card: 'Cards',
  carousal: 'Gallery',
  contact_us: 'Contact',
}

function getDifferenceInSeconds(dateString2) {
  // Convert date strings to Date objects
  const date1 = new Date()
  const date2 = new Date(dateString2)

  // Calculate the difference in milliseconds
  const difference = Math.abs(date1 - date2)

  // Convert milliseconds to seconds
  const differenceInSeconds = Math.floor(difference / 1000)

  return differenceInSeconds
}

function PublicSidebar({
  pages,
  parentData,
  searchData,
  setSearchData,
  setParentData,
  setActivePage,
  filterData,
  setFilterData,
  activePage,
  pageData,
  onGoBackToHomePage,
  couponCode,
  setCouponCode,
  bookingDetails,
  setBookingDetails,
  formsData,
  unitData,
  setUnitData,
  setFormData,
}) {
  const dispatch = useDispatch()
  const history = useHistory()

  const isDesktop = useMediaQuery('(min-width:900px)')
  const [collapsed, setCollapsed] = useState(false)
  const [isFirstToggle, setIsFirstToggle] = useState(true)
  const isSideBarOpen = useSelector((state) => state?.SideBar?.isHeaderSideBarOpen)
  const publicUser = useSelector((state) => state?.User?.publicUser)
  const { isTimerChanged } = useSelector((state) => state?.TimerStatus?.isTimerChanged)
  const [timeLeft, setTimeLeft] = useState(RESERVATION_DEFAULT_DURATION_SECONDS)
  const [hasActiveTimer, setHasActiveTimer] = useState(false)
  const toggleSidebar = () => {
    setCollapsed(!collapsed)
    setIsFirstToggle(false)
  }

  const expandPublicSidebar = () => {
    setCollapsed(false)
    setIsFirstToggle(false)
  }

  //* Close Sidebar If header Sidebar Open
  useEffect(() => {
    isSideBarOpen === true && setCollapsed(true)
  }, [isSideBarOpen])

  useEffect(() => {
    setCollapsed(isDesktop ? false : true)
  }, [isDesktop])

  useEffect(() => {
    let interval
    const domainId = parentData?.id
    if (!domainId) {
      setHasActiveTimer(false)
      return
    }
    const readRow = () => {
      try {
        const raw = getLocalStorageItem('timer')
        const storage_timer = raw && JSON.parse(raw)
        return storage_timer?.[domainId] || null
      } catch {
        return null
      }
    }
    const current_storage_timer = readRow()
    if (!current_storage_timer) {
      setHasActiveTimer(false)
      return
    }
    const duration = parseInt(current_storage_timer?.duration, 10) || 0
    if (getDifferenceInSeconds(current_storage_timer?.time) <= duration) {
      setHasActiveTimer(true)
      interval = setInterval(() => {
        const row = readRow()
        if (!row) {
          clearInterval(interval)
          setHasActiveTimer(false)
          return
        }
        const timeLeft = (parseInt(row.duration, 10) || 0) - getDifferenceInSeconds(row.time)
        if (timeLeft <= 0) {
          setHasActiveTimer(false)
          const sessionUuid = row.session_uuid
          if (sessionUuid) {
            request(`expire-reservation-session/${sessionUuid}`, 'GET')
          }
          clearInterval(interval)
          dispatch(setNotification({ type: 'error', message: 'Time is expired!' }))
          history.push(window.location.pathname)
          return
        }

        if (timeLeft == 60) {
          setCollapsed(false)
        }
        if (timeLeft === 120) {
          setCollapsed(false)
        }
      }, 1000)
    } else {
      setHasActiveTimer(false)
    }
    return () => clearInterval(interval)
  }, [isTimerChanged, parentData?.id, dispatch, history])

  const sidebarWidgetTypes = []
  pages?.forEach((page) => {
    page?.component_call_sections?.forEach((section) => {
      section?.widgets?.forEach((widget) => {
        const widgetType = widget?.component_name || widget?.name || ''
        if (widgetType && !sidebarWidgetTypes.includes(widgetType)) sidebarWidgetTypes.push(widgetType)
      })
    })
  })

  const showFilterFooterButton = sidebarWidgetTypes.includes('search_filters')
  const showLoginFooterButton = !publicUser?.email && (sidebarWidgetTypes.includes('login_form') || sidebarWidgetTypes.includes('public_auth'))
  const shouldHideMobileCollapsedSidebar = !isDesktop && collapsed && !hasActiveTimer
  const showAddTimeFooterButton = hasActiveTimer
  const showMobileFooterMenu = !isDesktop && collapsed && (showFilterFooterButton || showLoginFooterButton || showAddTimeFooterButton)

  return (
    <>
      <div
        className={`pt-5 sidebar sidebar ${collapsed ? 'collapsed' : ''} ${isFirstToggle ? 'first-toggle' : ''}`}
        style={
          isDesktop
            ? { borderRight: '1px solid #dee2e6' }
            : {
                borderRight: '1px solid #dee2e6',
              // Keep existing mobile behavior (fixed sidebar) so the strip is always visible.
              position: 'fixed',
              left: 0,
              top: 0,
                background: 'white',
                height: '100vh',
                zIndex: 2,
                paddingBottom: showMobileFooterMenu ? '4rem' : '0.5rem',
                overflow: 'hidden',
              display: shouldHideMobileCollapsedSidebar ? 'none' : 'block',
              }
        }>
        <button
          className={`btn h2 toggle-btn ${collapsed ? 'collapsed' : ''}`}
          style={{ color: primary_color, fontSize: isDesktop ? '2rem' : '1.5rem' }}
          onClick={toggleSidebar}>
          {collapsed ? <BiArrowToRight /> : <BiArrowToLeft />}
        </button>
        <div className='sidebar-content' style={isDesktop ? { maxHeight: '170vh', overflow: 'auto' } : { height: '90vh', overflow: 'auto' }}>
          {pages?.map((page, pageIndex) => {
            return (
              <React.Fragment key={page?.id || page?.uuid || page?.name || pageIndex}>
                {page?.component_call_sections?.map((section, index) => {
                  return (
                    <Col key={section?.id || section?.uuid || `${page?.id || pageIndex}-${index}`} md={parseInt(section?.col) || 6} style={{ padding: '0px' }}>
                      <PublicSection
                        filterData={filterData}
                        setFilterData={setFilterData}
                        section={section}
                        searchData={searchData}
                        setSearchData={setSearchData}
                        parentData={parentData}
                        setParentData={setParentData}
                        setActivePage={setActivePage}
                        activePage={activePage}
                        pageData={pageData}
                        onGoBackToHomePage={onGoBackToHomePage}
                        couponCode={couponCode}
                        setCouponCode={setCouponCode}
                        bookingDetails={bookingDetails}
                        formsData={formsData}
                        setFormData={setFormData}
                        setBookingDetails={setBookingDetails}
                        unitData={unitData}
                        setUnitData={setUnitData}
                        isSidebar={true}
                        sidebarCollapsed={collapsed}
                        onExpandPublicSidebar={expandPublicSidebar}
                        hideCollapsedCtas={!isDesktop}
                      />
                    </Col>
                  )
                })}
              </React.Fragment>
            )
          })}
        </div>
      </div>
      {!isDesktop && collapsed && (
        <button
          type='button'
          className='btn public-sidebar-floating-toggle'
          style={{ color: primary_color }}
          onClick={expandPublicSidebar}
          aria-label='Open sidebar'>
          <BiArrowToRight />
        </button>
      )}
      {!isDesktop && !collapsed && (
        <button
          type='button'
          className='btn public-sidebar-floating-close'
          style={{ color: primary_color }}
          onClick={toggleSidebar}
          aria-label='Collapse sidebar'>
          <BiArrowToLeft />
        </button>
      )}
      {showMobileFooterMenu && (
        <div className='public-sidebar-footer-menu'>
          {showAddTimeFooterButton && (
            <button type='button' className='btn public-sidebar-footer-menu__btn' onClick={expandPublicSidebar}>
              +2 min
            </button>
          )}
          {showFilterFooterButton && (
            <button type='button' className='btn public-sidebar-footer-menu__btn' onClick={expandPublicSidebar}>
              Filter
            </button>
          )}
          {showLoginFooterButton && (
            <button type='button' className='btn public-sidebar-footer-menu__btn' onClick={expandPublicSidebar}>
              Login
            </button>
          )}
        </div>
      )}
    </>
  )
}

export default PublicSidebar
